// // @ts-check
import { useMemo, useReducer, useCallback, useState } from 'react';
import merge from 'lodash.merge';
import { v4 as uuid } from 'uuid';
import * as Utils from './utils';

export const actions = {
    onChange: 'ON_CHANGE',
    onBlur: 'ON_BLUR',
    onPopulate: 'ON_POPULATE',
    onReset: 'ON_RESET',
    onSubmitError: 'ON_SUBMIT_ERROR',
    onManualSet: 'ON_MANUAL_SET',
    onTriggerValidation: 'TRIGGER_VALIDATION',
};

/**
 * 
 * @param {import('./types').FormV2Config} _config 
 * @param {Record<string, import('./types').FormV2FieldInput>} _fields 
 * @param {Record<string, any>} [_externalState] 
 */
const useFormV2 = (
    _config,
    _fields,
    _externalState
) => {
    const sld = (typeof window !== 'undefined' && typeof localStorage !== 'undefined' && localStorage?.getItem?.('secondaryLanguageDisabled') === 'yes') ? true : false;

    const availableLangs = useMemo(() => {
        let arr = ['en', 'se'];
        if (sld) arr = ['en'];

        return arr;
    }, [typeof window, sld])

    const config = useMemo(() => generateFormConfig(_config), []);
    const fields = useMemo(() => generateFormFields(_fields, availableLangs), []);
    const externalState = useMemo(() => _externalState || {}, [_externalState]);

    /**
     * @type {[import('./types').FormV2State, React.Dispatch<{payload: any; type: string; fields: Record<string, import('./types').FormV2Field>; externalState: Record<string, any>}>]}
     */
    const [state, dispatch] = useReducer(reducer, { fields, externalState }, getInitialState);
    const [submitError, setSubmitError] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    /**
     * @type {{tabs: string[]}} errors
     */
    const errors = useMemo(() => {
        const errs = {
            tabs: [],
        };

        for (const key in state) {
            const hasError = state?.[key]?.hasError ?? false;
            if (hasError) {
                const field = fields?.[key];
                if (field) {
                    const isShown = field.condition ? field.condition(state, externalState) : true;
                    if (!isShown) continue;
                    if (field.tab) {
                        if (!errs.tabs.includes(field.tab)) errs.tabs.push(field.tab);
                        continue;
                    }

                    if (field.section) {
                        if (config.sections?.[field.section]?.tab) {
                            if (!errs.tabs.includes(config.sections[field.section].tab)) errs.tabs.push(config.sections[field.section].tab);
                            continue;
                        }
                    }

                    if (field.subsection) {
                        if (config.sections) {
                            for (const sKey in config.sections) {
                                const section = config.sections[sKey];
                                if (section && section.subsections && section.subsections?.[field.subsection]) {
                                    if (section.tab && !errs.tabs.includes(section.tab)) errs.tabs.push(section.tab);
                                }
                            }
                        }
                    }
                }
            }
        }

        return errs;
    }, [state, fields, config, externalState]);

    const handleChange = useCallback((payload) => {
        dispatch({
            type: actions.onChange,
            payload,
            fields,
            externalState,
        });

        setSubmitError(null);
    }, [fields, externalState]);

    const handleBlur = useCallback((payload) => {
        dispatch({
            type: actions.onBlur,
            payload,
            fields,
            externalState
        });
    }, [fields, externalState]);

    const handleReset = useCallback((payload) => {
        dispatch({
            type: actions.onReset,
            payload,
            fields,
            externalState
        });

        setSubmitAttempted(false);
        setSubmitError(null);
    }, [fields, externalState]);

    const handlePopulate = useCallback((payload) => {
        dispatch({
            type: actions.onPopulate,
            payload,
            fields,
            externalState,
        })
    }, [fields, externalState]);

    const handleSubmit = async () => {
        setSubmitAttempted(true);
        setSubmitError(null);

        const { finalState, fullFinalState, error } = generateDataToSubmitFromState({ fields, state, externalState });

        if (error) {
            dispatch({
                type: actions.onSubmitError,
                payload: fullFinalState,
                fields,
                externalState
            });

            setSubmitError(error);
            return null;
        }

        const transformedState = await getTransformedDataFromState({ fields, state: finalState, externalState });
        const data = config?.skipFinalObjectTransformation ? transformedState : generateFinalDataObjectForSubmit(transformedState);

        if (config?.onBeforeSubmitValidator) {
            const validatorError = await config.onBeforeSubmitValidator(data, fullFinalState, externalState);
            if (validatorError) {
                dispatch({
                    type: actions.onSubmitError,
                    payload: fullFinalState,
                    fields,
                    externalState
                });

                setSubmitError(validatorError);
                return null;
            }
        }

        return data;
    }

    const set = useCallback((fieldName, value) => {
        dispatch({
            type: actions.onManualSet,
            payload: {
                name: fieldName,
                value,
            },
            fields,
            externalState,
        })
    }, [fields, externalState]);

    const triggerValidation = useCallback((fieldName = null) => {
        dispatch({
            type: actions.onTriggerValidation,
            payload: {
                name: fieldName,
            },
            fields,
            externalState,
        })
    }, [fields, externalState])

    return {
        config,
        fields,
        state,
        externalState,

        submitError,
        setSubmitError,
        submitAttempted,
        errors,
        availableLangs,

        handleChange,
        handleBlur,
        handleReset,
        handlePopulate,
        handleSubmit,

        set,
        triggerValidation
    }
}

export default useFormV2;

/**
 * 
 * @param {import('./types').FormV2State} state 
 * @param {any} action 
 * @returns {import('./types').FormV2State}
 */
export function reducer(state, action) {
    if (!action) return state;
    try {
        const {
            type,
            payload,
            fields,
            externalState,
        } = action;

        switch (type) {
            case actions.onChange: {
                const name = payload.name;
                if (!name) return state;

                const field = fields[name];
                const fState = state[name];
                if (!field || !fState) return state;

                const newState = {
                    ...state,
                    [name]: getFieldState({
                        field,
                        isTouched: ['checkbox', 'switch'].includes(field.type) ? true : fState.isTouched ?? false,
                        isRequired: payload.isRequired,
                        value: payload.value,
                        options: payload.options,
                        state,
                        externalState,
                    })
                };

                if (field.connectedFields && Array.isArray(field.connectedFields) && field.connectedFields.length > 0) {
                    for (const key of field.connectedFields) {
                        if (key === name) continue;
                        const connectedField = fields[key];
                        const connectedFstate = newState[key];

                        if (!connectedField || !connectedFstate) continue;

                        let isRequired = Utils.getIsRequired(connectedField.isRequired, newState, externalState);
                        let options = Utils.getOptions(connectedField.config?.options, newState, externalState);

                        newState[key] = getFieldState({
                            field: connectedField,
                            isTouched: connectedFstate.isTouched ?? false,
                            isRequired,
                            value: connectedFstate.value,
                            options,
                            state: newState,
                            externalState,
                        });
                    }
                }

                return newState;
            };

            case actions.onBlur: {
                const name = payload.name;
                if (!name) return state;

                const field = fields[name];
                const fState = state[name];
                if (!field || !fState) return state;

                const newState = {
                    ...state,
                    [name]: getFieldState({
                        field,
                        isTouched: true,
                        value: fState.value,
                        isRequired: payload.isRequired,
                        state,
                        externalState,
                        options: payload.options,
                    })
                }

                if (field.connectedFields && Array.isArray(field.connectedFields) && field.connectedFields.length > 0) {
                    for (const key of field.connectedFields) {
                        if (key === name) continue;
                        const connectedField = fields[key];
                        const connectedFstate = newState[key];

                        if (!connectedField || !connectedFstate) continue;

                        let isRequired = Utils.getIsRequired(connectedField.isRequired, newState, externalState);
                        let options = Utils.getOptions(connectedField.config?.options, newState, externalState);

                        newState[key] = getFieldState({
                            field: connectedField,
                            isTouched: true,
                            isRequired,
                            value: connectedFstate.value,
                            options,
                            state: newState,
                            externalState,
                        });
                    }
                }

                return newState;
            };

            case actions.onPopulate: {
                const newState = populateData({
                    fields,
                    state,
                    externalState,
                    data: payload
                });

                return newState;
            };

            case actions.onReset: {
                const initialState = getInitialState({ fields, externalState });
                return initialState ?? state;
            };

            case actions.onSubmitError: {
                return payload ?? state;
            };

            case actions.onManualSet: {
                const name = payload.name;
                if (!name) return state;

                const field = fields[name];
                const fState = state[name];
                if (!field || !fState) return state;

                const isRequired = Utils.getIsRequired(field.isRequired, state, externalState);
                const options = Utils.getOptions(field.config?.options, state, externalState);

                const newState = {
                    ...state,
                    [name]: getFieldState({
                        field,
                        isTouched: fState.isTouched ?? false,
                        value: payload.value,
                        isRequired,
                        options,
                        state,
                        externalState,
                    })
                }

                if (field.connectedFields && Array.isArray(field.connectedFields) && field.connectedFields.length > 0) {
                    for (const key of field.connectedFields) {
                        if (key === name) continue;
                        const connectedField = fields[key];
                        const connectedFstate = newState[key];

                        if (!connectedField || !connectedFstate) continue;

                        let isRequired = Utils.getIsRequired(connectedField.isRequired, newState, externalState);
                        let options = Utils.getOptions(connectedField.config?.options, newState, externalState);

                        newState[key] = getFieldState({
                            field: connectedField,
                            isTouched: connectedFstate.isTouched ?? false,
                            isRequired,
                            value: connectedFstate.value,
                            options,
                            state: newState,
                            externalState,
                        });
                    }
                }

                return newState;
            };

            case actions.onTriggerValidation: {
                if (payload?.name) {
                    let fieldsToLoop = [];
                    if (typeof payload.name === 'string') fieldsToLoop.push(payload.name);
                    else if (Array.isArray(payload.name)) fieldsToLoop = payload.name;

                    if (Array.isArray(fieldsToLoop) && fieldsToLoop.length > 0) {
                        const newState = { ...state };

                        for (const fieldName of fieldsToLoop) {
                            const field = fields[fieldName];
                            const fState = state[fieldName];
                            if (!field || !fState) continue;

                            const isRequired = Utils.getIsRequired(field.isRequired, state, externalState);
                            const options = Utils.getOptions(field.config?.options, state, externalState);

                            newState[fieldName] = getFieldState({
                                field,
                                isTouched: fState.isTouched ?? false,
                                value: fState.value,
                                isRequired,
                                options,
                                state,
                                externalState,
                            });

                            if (field.connectedFields && Array.isArray(field.connectedFields) && field.connectedFields.length > 0) {
                                for (const key of field.connectedFields) {
                                    if (key === fieldName) continue;
                                    const connectedField = fields[key];
                                    const connectedFstate = newState[key];

                                    if (!connectedField || !connectedFstate) continue;

                                    let isRequired = Utils.getIsRequired(connectedField.isRequired, newState, externalState);
                                    let options = Utils.getOptions(connectedField.config?.options, newState, externalState);

                                    newState[key] = getFieldState({
                                        field: connectedField,
                                        isTouched: connectedFstate.isTouched ?? false,
                                        isRequired,
                                        value: connectedFstate.value,
                                        options,
                                        state: newState,
                                        externalState,
                                    });
                                }
                            }
                        }

                        return newState;
                    } else {
                        return state;
                    }
                }
                const { fullFinalState } = generateDataToSubmitFromState({ fields, state, externalState });
                return fullFinalState ?? state;
            };

            default: {
                console.log('--- useForm reducer default action: ', action);
                return state;
            }
        }
    } catch (err) {
        console.log('--- useForm reducer err: ', err);
        return state;
    }
}

/**
 * 
 * @param {{
 *   field: import('./types').FormV2Field;
 *   isTouched: boolean;
 *   value: any;
 *   isRequired: boolean;
 *   options?: import('./types').FormV2Options;
 *   state: import('./types').FormV2State;
 *   externalState: Record<string, any>;
 * }} param0
 *  @returns {{value: any; isTouched: boolean; isValid: boolean; hasError: boolean; errorMessage?: string | undefined;}}
 */
export function getFieldState({
    field,
    isTouched,
    value,
    isRequired,
    options,
    state = {},
    externalState,
}) {
    let isValid = true;
    let errorMessage;

    try {
        const validators = field.validators;
        const config = field.config;

        if (isRequired && validators.required) {
            const e = validators.required(value, config);
            if (e) {
                isValid = false;
                errorMessage = e;
            }
        }

        if (isValid && validators.type) {
            const e = validators.type(value, config);
            if (e) {
                isValid = false;
                errorMessage = e;
            }
        }

        if (isValid && validators.value) {
            const e = validators.value(value, config, options);
            if (e) {
                isValid = false;
                errorMessage = e;
            }
        }

        if (isValid && validators.custom) {
            const e = validators.custom(value, state, externalState, config, options);
            if (e) {
                isValid = false;
                errorMessage = e;
            }
        }
    } catch (err) {
        console.log('--- getFieldState err: ', err);
    }

    return {
        value,
        isTouched,
        isValid,
        hasError: !isValid && isTouched ? true : false,
        errorMessage: !isValid && isTouched && errorMessage ? errorMessage : undefined,
    }
}

/**
 * 
 * @param {{
 *   fields: Record<string, import('./types').FormV2Field>;
 *   state: import('./types').FormV2State;
 *   externalState: Record<string, any>;
 *   data: Record<string, any>;
 * }} param0 
 */
export function populateData({
    fields,
    state,
    externalState,
    data
}) {
    const newState = {};

    try {
        for (const key in fields) {
            const field = fields[key];
            const fState = state[key];
            if (!field || !fState) continue;

            let value = Utils.getValueFromObject(data, key);

            // ! TODO add logic for file when file type is added - if single file the value is null
            if (typeof value !== 'undefined' && field?.type !== 'password' && value !== null) {
                const isRequired = Utils.getIsRequired(field.isRequired, state, externalState);
                const options = Utils.getOptions(field.config?.options, state, externalState);

                let skip = false;

                switch (field.type) {
                    case 'number': {
                        if (Utils.isValidNumber(value)) value = String(value);
                        else value = '';

                        break;
                    };

                    case 'checkbox': case 'switch': {
                        if (typeof value === 'boolean' && value) value = true;
                        else value = false;

                        break;
                    };

                    case 'ssn': {
                        if (typeof value === 'string') {
                            const defaultCountry = typeof window !== 'undefined' ? localStorage?.getItem?.('country') : null;

                            const newValue = Utils.getSSNValue(value, defaultCountry);
                            if (!newValue) {
                                skip = true;
                            } else {
                                value = newValue;
                            }
                        }
                        else {
                            skip = true;
                        }

                        break;
                    };

                    case 'date': {
                        if (Utils.isObject(value) || typeof value === 'string') {
                            if (typeof value === 'string') {
                                if (!Utils.isValidDate(value)) skip = true;
                            } else {
                                if (value?.date) {
                                    const newValue = Utils.getDateValue(value.date);
                                    if (newValue) {
                                        value = newValue;
                                    } else {
                                        skip = true;
                                    }
                                } else {
                                    skip = true;
                                }
                            }
                        } else {
                            skip = true;
                        }

                        break;
                    }
                }

                if (skip) newState[key] = state[key];

                newState[key] = getFieldState({
                    field,
                    isTouched: false,
                    value,
                    isRequired,
                    options,
                    state,
                    externalState,
                })
            } else {
                newState[key] = state[key];
            }
        }
    } catch (err) {
        console.log('--- populateData err: ', err);
    }

    return newState;
}

/**
 * 
 * @param {{
 *   fields: Record<string, import('./types').FormV2Field>;
 *   externalState: Record<string, any>;
 * }} param0 
 */
export function getInitialState({
    fields,
    externalState,
}) {
    const state = {};

    try {
        for (const key in fields) {
            try {
                const field = fields[key];
                if (!field) continue;

                const value = field.initialValue;
                const isRequired = typeof field.isRequired === 'boolean' ? field.isRequired : false;
                const options = Array.isArray(field.config?.options) ? Utils.getOptions(field.config.options, {}, externalState) : undefined;

                state[key] = getFieldState({
                    field,
                    isTouched: false,
                    value,
                    isRequired,
                    options,
                    externalState,
                })
            } catch (err) {
                console.log('--- getInitialState field err: ', fields?.[key], err);
                continue;
            }
        }
    } catch (err) {
        console.log('--- getInitialState err: ', err);
    }

    return state;
}

/**
 * 
 * @param {import('./types').FormV2Config} conf 
 * @returns {import('./types').FormV2Config}
 */
export const generateFormConfig = (conf = {}) => ({
    ...conf,
    id: conf?.id || uuid(),
    tabs: conf?.tabs ? Object.keys(conf?.tabs).reduce((obj, key) => {
        obj[key] = {
            ...conf?.tabs[key],
            id: conf?.tabs[key].id || uuid()
        };

        return obj;
    }, {}) : undefined,
    sections: conf?.sections ? Object.keys(conf?.sections).reduce((obj, key) => {
        obj[key] = {
            ...(conf?.sections[key] || {}),
            id: conf?.sections[key].id || uuid(),
            subsections: conf?.sections[key].subsections ? Object.keys(conf?.sections[key].subsections).reduce((obj, key2) => {
                obj[key2] = {
                    ...(conf?.sections[key].subsections[key2] || {}),
                    id: conf?.sections[key].subsections[key2].id || uuid()
                };

                return obj;
            }, {}) : undefined,
        };

        return obj;
    }, {}) : undefined,
});

/**
 * 
 * @param {Record<string, import('./types').FormV2FieldInput>} inputFields
 * @param {string[]} availableLangs
 * @returns {Record<string, import('./types').FormV2Field>}
 */
export const generateFormFields = (
    inputFields,
    availableLangs
) => {
    try {
        /**
         * @type {Record<string, import('./types').FormV2Field>}
         */
        const fields = {};

        for (const key in inputFields) {
            const obj = inputFields[key];

            const isMultilang = obj.isMultilang ?? false;
            const id = obj.id || uuid();

            let initialValue = obj.initialValue;

            let requiredValidator = (value) => Utils.isEmpty(value) ? 'This field is required!' : null;
            let typeValidator;
            let valueValidator;
            let customValidator = obj.validator;

            let instantTransformer;
            let submitTransformer;
            let customInstantTransformer = obj.instantTransformer;
            let customSubmitTransformer = obj.submitTransformer;

            switch (obj.type) {
                case 'checkbox': case 'switch': {
                    if (typeof initialValue === 'undefined') initialValue = false;

                    typeValidator = (value) => ['boolean', 'undefined'].includes(typeof value) ? null : 'Invalid input!';

                    break;
                };

                case 'number': {
                    if (typeof initialValue === 'number') initialValue = initialValue.toString();
                    else if (typeof initialValue !== 'string') initialValue = '';

                    typeValidator = (value) => value === '' || Utils.isValidNumber(value) ? null : 'Please enter a valid number!';
                    valueValidator = (value, config) => {
                        if (value === '') return null;
                        if (Utils.isValidNumber(config?.min)) {
                            if (+value < +config.min) return 'Entered number must be higher than {min}!';
                        }
                        if (Utils.isValidNumber(config?.max)) {
                            if (+value > +config.max) return 'Entered number must be lower than {max}!';
                        }

                        return null;
                    }

                    instantTransformer = (value) => {
                        let val = value.replace(/[^0-9.]/g, '');
                        if (val === '.') val = '0.';
                        if (val.startsWith?.('.')) val = '0.' + val.replace(/\./g, '');
                        const dotIndex = val.indexOf('.');
                        if (dotIndex !== -1) val = val.slice(0, dotIndex + 1) + val.slice(dotIndex + 1).replace(/\./g, '');
                        return val;
                    }
                    submitTransformer = (value) => value === '' ? value : +value;

                    break;
                };

                case 'email': {
                    if (!initialValue || !Utils.isValidEmail(initialValue)) initialValue = '';

                    typeValidator = (value) => value === '' || Utils.isValidEmail(value) ? null : 'Please enter a valid email address!';

                    instantTransformer = (value) => value.trim().toLocaleLowerCase().replace(/[^a-z0-9._+@-]/g, '');
                    submitTransformer = (value) => value.trim().toLocaleLowerCase().replace(/[^a-z0-9._+@-]/g, '');

                    break;
                };

                case 'password': {
                    if (typeof initialValue !== 'string') initialValue = '';

                    if (obj?.config?.passwordV2) typeValidator = (value) => value === '' || Utils.isValidPasswordV2(value) ? null : 'Password must be at least 8 characters long, must have at least one upper case letter, one lower case letter and one number!';
                    else typeValidator = (value) => value === '' || Utils.isValidPassword(value) ? null : 'Password must be at least 10 characters long, must have at least one upper case letter, one lower case letter, one number and one special character (allowed special characters are: !?#&*)!';
                    instantTransformer = (value) => value.trim().replace(/ /g, '');
                    submitTransformer = (value) => value.trim().replace(/ /g, '');

                    break;
                };

                case 'url': {
                    if (!initialValue || !Utils.isValidUrl(initialValue)) initialValue = '';

                    typeValidator = (value) => value === '' || Utils.isValidUrl(value) ? null : 'Please enter a valid URL!';
                    instantTransformer = (value) => value.trim().toLocaleLowerCase().replace(/ /g, '');
                    submitTransformer = (value) => value.trim().toLocaleLowerCase().replace(/ /g, '');

                    break;
                };

                case 'select': case 'radio': {
                    if (!initialValue || typeof initialValue !== 'string') initialValue = '';

                    valueValidator = (value, _, options) => value === '' || options?.find?.(obj => obj?.value === value) ? null : 'Please select one of the provided values!';

                    break;
                };

                case 'code': {
                    initialValue = '';

                    typeValidator = (value) => value === '' || Utils.isValidCode(value) ? null : 'Please enter a valid code!';

                    instantTransformer = (value) => value.replace(/[^0-9]/g, '').slice(0, 6);
                    submitTransformer = (value) => value.replace(/[^0-9]/g, '').slice(0, 6);

                    break;
                };

                case 'text': case 'textarea': case 'phone': {
                    if (typeof initialValue !== 'string') initialValue = '';

                    if (!initialValue && obj.type === 'phone') {
                        /**
                         * @type {('uk' | 'sweden' | 'denmark') | null}
                         */
                        const country = typeof window !== 'undefined' ? localStorage?.getItem?.('country') : null;
                        /**
                         * @type {('gb' | 'es' | 'gr' | 'ch' | 'ae') | null}
                         */
                        const countryTwoLetter = typeof window !== 'undefined' ? localStorage?.getItem?.('countryTwoLetter') : null;
                        if (country === 'uk') {
                            if (countryTwoLetter) {
                                switch (countryTwoLetter) {
                                    case 'ae': initialValue = '971'; break;
                                    case 'ch': initialValue = '41'; break;
                                    case 'es': initialValue = '34'; break;
                                    case 'gr': initialValue = '30'; break;
                                    case 'gb': initialValue = '44'; break;
                                }
                            } else {
                                initialValue = '44';
                            }
                        }

                        if (country === 'sweden') initialValue = '46';
                        if (country === 'denmark') initialValue = '45';
                    }

                    typeValidator = (value) => typeof value === 'string' ? null : 'Invalid input!';
                    valueValidator = (value, config) => {
                        if (value === '') return null;
                        if (Utils.isValidNumber(config?.min)) {
                            if (value.length < config.min) return config?.type === 'phone' ? 'Please enter a valid phone number!' : 'Entered text must be longer than {min} characters!';
                        }
                        if (Utils.isValidNumber(config?.max)) {
                            if (value.length > config.max) return config?.type === 'phone' ? 'Please enter a valid phone number!' : 'Entered text must be shorter than {max} characters!';
                        }

                        return null;
                    }

                    if (obj.type === 'phone') {
                        instantTransformer = (value) => value.replace(/[^0-9+]/g, '');
                        submitTransformer = (value) => {
                            value = value.replace(/[^0-9+]/g, '');
                            if (value.startsWith('00')) value = '+' + value.slice(2);
                            if (!value.startsWith('+') && value.length > 3) value = '+' + value;
                            return value;
                        }
                    }

                    break;
                };

                case 'date': {
                    initialValue = obj?.initialValue ? Utils.isValidDate(obj.initialValue) ? obj.initialValue : '' : '';

                    typeValidator = (value) => value === '' || Utils.isValidDate(value) ? null : 'Please enter a valid date!';

                    /**
                     * @type {(value: string, config: import('./types').FormV2FieldConfig) => string | null}
                     */
                    valueValidator = (value, config) => {
                        if (value === '') return null;

                        if (Utils.isValidDate(value)) {
                            const date = new Date(value);

                            if (Utils.isValidDate(config?.min)) {
                                const minDate = new Date(config.min);
                                if (date < minDate) return 'Entered date must be higher than {min}!';
                            }

                            if (Utils.isValidDate(config?.max)) {
                                const maxDate = new Date(config.max);
                                if (date > maxDate) return 'Entered date must be lower than {max}!';
                            }

                            if (config?.dateType) {
                                const now = new Date();
                                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                                if (config.dateType === 'dob' || config.dateType === 'only-past') {
                                    if (date > today) return config.dateType === 'dob' ? 'Please enter a valid date of birth!' : 'Only past dates are allowed!';
                                } else if (config.dateType === 'only-future') {
                                    if (date < today) return 'Only future dates are allowed!';
                                }
                            }
                        } else {
                            return 'Please enter a valid date!';
                        }

                        return null;
                    }

                    instantTransformer = (val) => {
                        // console.log('val: ', val)
                        if (val && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
                            const [y, m, d] = val.split('-');
                            return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        }

                        return '';
                        // if (val) {
                        //     let [y, m, d] = val.split('-');
                        //     if (typeof y === 'string' && y.length >= 4) {
                        //         y = y.slice(0, 4);
                        //     }

                        //     return `${y ? String(y).padStart(4, '0') : ''}-${m ? String(m).padStart(2, '0') : '01'}-${d ? String(d).padStart(2, '0') : '01'}`;
                        // }

                        // return val;
                    }

                    break;
                };

                case 'ssn': {
                    initialValue = {
                        country: obj?.initialValue?.country || (typeof window !== 'undefined' ? localStorage?.getItem?.('country') : null) || 'sweden',
                        ssn: obj?.initialValue?.ssn || '',
                    }

                    requiredValidator = (value) => value && Utils.isObject(value) && value.country && value.ssn && typeof value.ssn === 'string' && value.ssn.trim().replace(/^[0-9]/g, '').length > 0 ? null : 'Please enter a valid SSN!';

                    typeValidator = (value) => value && Utils.isObject(value) ? null : 'Please enter a valid SSN!';

                    valueValidator = (value) => Utils.isValidSSN(value) ? null : 'Please enter a valid SSN!';

                    instantTransformer = (value) => {
                        let country = value?.country || (typeof window !== 'undefined' ? localStorage?.getItem?.('country') : null) || 'sweden';
                        let ssn = value?.ssn || '';
                        ssn = ssn.replace(/[^0-9]/g, '');
                        if (country === 'sweden') ssn = ssn.slice(0, 12);
                        if (country === 'denmark') ssn = ssn.slice(0, 10);

                        return {
                            country,
                            ssn,
                        }
                    }

                    break;
                };

                case 'custom': {
                    requiredValidator = null;
                    break;
                };
            };

            /**
             * @type {import('./types').FormV2FieldConfig}
             */
            const config = {
                ...(obj.config || {}),
                id,
                isMultilang,
                type: obj.type,
            };

            if (isMultilang) {
                for (const lang of availableLangs) {
                    fields[`${key}.${lang}`] = {
                        id: `${id}.${lang}`,
                        name: `${key}.${lang}`,
                        type: obj.type,
                        classes: obj.classes,
                        cols: obj.cols,
                        condition: obj.condition,
                        component: obj.component,
                        connectedFields: obj.connectedFields,
                        description: obj.description,
                        info: obj.info,
                        initialValue,
                        isDisabled: obj.isDisabled,
                        isMultilang,
                        isRequired: obj.isRequired,
                        label: obj.label,
                        placeholder: obj.placeholder,
                        section: obj.section,
                        subsection: obj.subsection,
                        tab: obj.tab,
                        styles: obj.styles,
                        validators: {
                            custom: customValidator,
                            required: requiredValidator,
                            type: typeValidator,
                            value: valueValidator,
                        },
                        callbacks: {
                            onChange: obj.onChangeCallback,
                            onFocus: obj.onFocusCallback,
                            onBlur: obj.onBlurCallback,
                        },
                        config: {
                            ...config,
                            lang,
                        },
                        transformers: {
                            instant: instantTransformer,
                            customInstant: customInstantTransformer,
                            submit: submitTransformer,
                            customSubmit: customSubmitTransformer,
                        }
                    };
                }
            } else {
                fields[key] = {
                    id,
                    name: key,
                    type: obj.type,
                    classes: obj.classes,
                    cols: obj.cols,
                    condition: obj.condition,
                    component: obj.component,
                    connectedFields: obj.connectedFields,
                    description: obj.description,
                    info: obj.info,
                    initialValue,
                    isDisabled: obj.isDisabled,
                    isMultilang,
                    isRequired: obj.isRequired,
                    label: obj.label,
                    placeholder: obj.placeholder,
                    section: obj.section,
                    subsection: obj.subsection,
                    tab: obj.tab,
                    styles: obj.styles,
                    validators: {
                        custom: customValidator,
                        required: requiredValidator,
                        type: typeValidator,
                        value: valueValidator,
                    },
                    callbacks: {
                        onChange: obj.onChangeCallback,
                        onFocus: obj.onFocusCallback,
                        onBlur: obj.onBlurCallback,
                    },
                    config,
                    transformers: {
                        instant: instantTransformer,
                        customInstant: customInstantTransformer,
                        submit: submitTransformer,
                        customSubmit: customSubmitTransformer,
                    }
                }
            }
        }

        return fields;
    } catch (err) {
        console.log('--- generateFormFields err: ', err);
        return {};
    }
}

/**
 * 
 * @param {{
 *   fields: Record<string, import('./types').FormV2Field>;
 *   state: import('./types').FormV2State;
 *   externalState: Record<string, any>;
 * }} param0 
 * @returns {{
 *   finalState: Record<string, any>;
 *   fullFinalState: Record<string, any>;
 *   error: (string | null);
 * }}
 */
export function generateDataToSubmitFromState({
    fields,
    state,
    externalState,
}) {
    const finalState = {};
    const fullFinalState = {};
    let error = null;

    try {
        if (!fields || !state) {
            error = 'Something went wrong! Please contact developers!';
            return {
                finalState,
                fullFinalState,
                error,
            }
        }

        for (const key in fields) {
            const field = fields[key];
            if (!field || !field.name) continue;

            const fState = state[field.name];
            if (!fState) continue;

            const isRequired = Utils.getIsRequired(field.isRequired, state, externalState);
            const options = Utils.getOptions(field.config?.options, state, externalState);

            const newFState = getFieldState({
                field,
                isTouched: true,
                value: fState.value,
                isRequired,
                options,
                state,
                externalState,
            });

            if (!newFState) continue;
            fullFinalState[field.name] = newFState;

            if (field.condition && !field.condition(state, externalState)) continue;

            finalState[field.name] = newFState;
            if (newFState.hasError) error = 'Please fill in all required fields correctly!';
        }
    } catch (err) {
        console.log('--- generateDataToSubmitFromState err: ', err);
    }

    return {
        finalState,
        fullFinalState,
        error,
    }
}

/**
 * 
 * @param {{
*   fields: Record<string, import('./types').FormV2Field>;
*   state: import('./types').FormV2State;
*   externalState: Record<string, any>;
* }} param0 
* @returns {Promise<Record<string, any>>}
*/
export async function getTransformedDataFromState({
    fields,
    state,
    externalState,
}) {
    const transformedState = {};

    try {
        for (const key in fields) {
            const field = fields[key];
            if (!field || !field.name) continue;

            const fState = state[field.name];
            if (!fState) continue;

            let value = fState.value;

            const options = Utils.getOptions(field.config?.options, state, externalState);

            if (field.transformers?.customSubmit) value = await field.transformers.customSubmit(value, state, externalState, field.config, options);
            if (field.transformers?.submit) value = await field.transformers.submit(value, state, externalState, field.config, options);

            transformedState[field.name] = value;
        }
    } catch (err) {
        console.log('--- getTransformedDataFromState err: ', err);
    }

    return transformedState;
}

/**
 * 
 * @param {Record<string, any>} state
 * @returns {Record<string, any>}
 */
export function generateFinalDataObjectForSubmit(state) {
    const obj = {};

    try {
        for (const key in state) {
            const value = state[key];
            if (key.includes('.')) {
                const [v, k] = Utils.stringWithDotToObject(key, value);
                const merged = { ...merge(obj[k], v) };
                obj[k] = merged;
            } else {
                obj[key] = value;
            }
        }
    } catch (err) {
        console.log('--- generateFinalDataObjectForSubmit err: ', err);
    }

    return obj;
}